import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskedLines, Reveal, EASE } from "@/lib/motion";
import { CTABand, EditorialMarquee } from "@/components/site/Bands";
import { SERVICES } from "@/data/site";
import { useBooking } from "@/lib/booking";
import { SEO, breadcrumbSchema } from "@/components/site/SEO";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
];

const SERVICE_HREF = {
  "content-strategy": "/services/content-strategy",
  "blog-content": "/services/blog-content",
  "conversion-assets": "/services/conversion-content",
  "content-ops": "/services/content-operations",
};

const PageHero = () => {
  const { openBooking } = useBooking();
  return (
    <section data-testid="services-hero" className="pt-6 pb-20 md:pb-28 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <p className="text-xs uppercase tracking-[0.25em] text-[#999999] mb-8 font-mono">Services</p>
        <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.98] tracking-tight text-[#111111]">
          <MaskedLines lines={["Your Organic,", "Predictable Growth"]} delay={0.1} />
          <span className="reveal-line">
            <motion.span
              className="block italic"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.1 + 2 * 0.12 }}
            >
              Marketing Engine.
            </motion.span>
          </span>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.7 }}
          className="mt-8 max-w-2xl text-lg md:text-xl text-[#666666] leading-relaxed"
        >
          Full-funnel growth systems from strategy and planning to execution and analytics. We offer
          AEO/GEO/SEO content writing services including blog articles, landing pages, case studies,
          social, and email. Our proven playbooks and AI-enabled workflows make your engine repeatable
          and scalable.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.85 }}
          className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4"
        >
          <button type="button" onClick={openBooking} data-testid="services-cta-audit" className="inline-flex items-center gap-2 bg-[#111111] text-white font-semibold px-8 py-4 hover:bg-[#333333] transition-colors group">
            Schedule a Content Audit
            <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
          </button>
          <Link to="/contact" data-testid="services-cta-talk" className="inline-flex items-center gap-2 border border-[#111111] text-[#111111] font-semibold px-8 py-4 hover:bg-[#F5F5F5] transition-colors">
            Talk to Us
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default function Services() {
  return (
    <div data-testid="services-page">
      <SEO
        title="B2B SaaS Content Services — Strategy, SEO, AEO/GEO"
        description="Full-funnel growth systems for B2B SaaS: content strategy, SEO/AEO/GEO blog articles, conversion assets, and content operations that scale."
        path="/services"
        jsonLd={[breadcrumbSchema(CRUMBS)]}
      />
      <Breadcrumbs items={CRUMBS} />
      <PageHero />
      <EditorialMarquee text="STRATEGY · SEO · AEO · GEO · CONVERSION · " speed={20} />

      <section id="services-list" className="section-pad bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-3xl text-[#111111] mb-16">
              Your Full Growth Engine.
            </h2>
          </Reveal>

          <div className="border-t border-[#111111]">
            {SERVICES.map((s) => (
              <motion.div
                key={s.id}
                id={s.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, ease: EASE }}
                className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 py-12 md:py-16 border-b border-[#E0E0E0] scroll-mt-28"
                data-testid={`service-block-${s.id}`}
              >
                <div className="md:col-span-5">
                  <span className="font-serif text-6xl md:text-7xl text-[#E0E0E0] leading-none">{s.n}</span>
                  <h3 className="mt-6 font-serif text-3xl md:text-4xl text-[#111111]">{s.title}</h3>
                  <p className="mt-5 text-[#666666] text-lg leading-relaxed">{s.desc}</p>
                  {SERVICE_HREF[s.id] ? (
                    <Link to={SERVICE_HREF[s.id]} className="inline-flex items-center gap-1 mt-8 text-sm font-semibold text-[#111111] link-underline" data-testid={`svc-learn-${s.id}`}>
                      Learn More <ArrowRight size={14} />
                    </Link>
                  ) : (
                    <span className="inline-flex items-center gap-1 mt-8 text-sm font-semibold text-[#999999]" data-testid={`svc-learn-${s.id}`}>
                      Coming soon <ArrowRight size={14} />
                    </span>
                  )}
                </div>
                <div className="md:col-span-6 md:col-start-7">
                  <p className="text-xs uppercase tracking-[0.15em] text-[#999999] mb-6">What&apos;s included</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                    {s.deliverables.map((d) => (
                      <li key={d} className="flex items-start gap-3 py-3 border-b border-[#E0E0E0] text-[#111111]">
                        <span className="text-[#999999] font-mono text-sm mt-0.5">→</span>
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTABand heading="Not sure which service you need? Start with a content audit." showSecondary={true} showCaseStudies={false} />
    </div>
  );
}
